// @flow

import _ from 'lodash/fp';
import React from 'react';
import ModalDialog from '../ModalDialog/ModalDialog';
import ServerError from '../ServerError/ServerError';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import Checkbox from '../Checkbox/Checkbox';
import NumberFormat from 'react-number-format';
import ReactTooltip from 'react-tooltip';
import Button from '../Button/Button';
import classNames from 'classnames';
import './AdvancedProductSlotDialog.css';

const mapWithIndex = _.map.convert({cap: false});

const hasChecked = modifiedData => v =>
  !!(modifiedData[v.uuid] || {}).checked;

export type ReadonlyVariantProps = {
  variant: Object,
  toggleVariant: Function,
  modifiedData: Object,
};

const ReadonlyVariant = ({
  variant,
  toggleVariant,
  modifiedData,
}: ReadonlyVariantProps) => {
  const variantData = modifiedData[variant.uuid] || {};
  return (
    <div className="row">
      <div className="column">
        <div className="row variant-row">
          {!!variant.hasRelated ? (
            <div
              className="shrink column"
              onClick={() => toggleVariant(variant, !variantData.expanded)}>
              <i className={`fa fa-${!!variantData.expanded ? 'minus' : 'plus'}`} />
            </div>
          ) : (
            <div className="shrink column">
              <i className="fa fa-plus disabled" />
            </div>
          )}
          <div className="small-5 column">
            {variant.name} ({variant.itemNumber})
          </div>
          <div className="small-3 column">
            {!!variantData.quantity && `Antal: ${variantData.quantity}`}
          </div>
          <div className="small-3 column">
            {!!variantData.unit && `Enhet: ${variantData.unit}`}
          </div>
        </div>
        {!!variantData.expanded && !!variantData.related && !!variantData.related.length && (() => {
          const checked = hasChecked(modifiedData);
          const checkedProducts = _.filter(p =>
            _.some(checked, p.variants), variantData.related);
          return (
            <div className="row related-row">
              <div className="column">
                {_.map(p => (
                  <ReadonlyProduct
                    key={p.id}
                    product={p}
                    toggleVariant={toggleVariant}
                    modifiedData={modifiedData} />
                ), !!checkedProducts.length ? checkedProducts : variantData.related)}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export type ReadonlyProductProps = {
  product: Object,
  toggleVariant: Function,
  modifiedData: Object,
};

const ReadonlyProduct = ({
  product,
  toggleVariant,
  modifiedData,
}: ReadonlyProductProps) => {
  const checkedVariants = _.filter(hasChecked(modifiedData), product.variants);
  return (
    <div className="row">
      <div className="column">
        <div className="row readonly-product-row">
          <div className="shrink column">
            <i className="fa fa-plus disabled" />
          </div>
          <div className="column">
            {product.name}
          </div>
        </div>
        <div className="row variants-row">
          <div className="column">
            {_.map(v => (
              <ReadonlyVariant
                key={v.uuid}
                variant={v}
                toggleVariant={toggleVariant}
                modifiedData={modifiedData} />
            ), !!checkedVariants.length ? checkedVariants : product.variants)}
          </div>
        </div>
      </div>
    </div>
  );
};

export type VariantProps = {
  variant: Object,
  navigateToVariant: Function,
  updateRelatedVariant: Function,
  toggleVariant: Function,
  modifiedData: Object,
  isStandard: boolean,
  mainVariantId: number,
};

const Variant = ({
  variant,
  navigateToVariant,
  updateRelatedVariant,
  toggleVariant,
  modifiedData,
  isStandard,
  mainVariantId,
}: VariantProps) => {
  const variantData = modifiedData[variant.uuid] || {};
  const checkDisabled = !!isStandard && mainVariantId === variant.id;
  return (
    <div className="row">
      <div className="column">
        <div className="row variant-row center-vert">
          {!!variant.hasRelated ? (
            <div
              className="shrink column"
              onClick={() => toggleVariant(variant, !variantData.expanded)}>
              <i className={`fa fa-${!!variantData.expanded ? 'minus' : 'plus'}`} />
            </div>
          ) : (
            <div className="shrink column">
              <i className="fa fa-plus disabled" />
            </div>
          )}
          <div className="small-4 column">
            {variant.name} ({variant.itemNumber})
          </div>
          <div className="small-2 column">
            <div
              data-tip
              data-for={`checked-disabled-tip-${variant.id}`}>
              <Checkbox
                name={`variant-${variant.id}-enabled`}
                checked={!!variantData.checked}
                disabled={checkDisabled}
                onChange={e => updateRelatedVariant(variant, {checked: e.target.checked})}
                id={`variant-${variant.id}-enabled`} />
              <label htmlFor={`variant-${variant.id}-enabled`}>
                Valbar
              </label>
              {!!checkDisabled && (
                <ReactTooltip
                  id={`checked-disabled-tip-${variant.id}`}
                  effect="solid">
                  <span>Kan ej välja bort varianten då det är en visningsartikel</span>
                </ReactTooltip>
              )}
            </div>
          </div>
          <div className="small-2 column price-input">
            <div className="price-label">
              Avvikande pris
            </div>
            <NumberFormat
              value={variantData.customPrice}
              onValueChange={(val, evt) => {
                updateRelatedVariant(variant, {
                  checked: true,
                  customPrice: _.isEmpty(val.value) ? null : val.floatValue,
                });
              }}
              decimalSeparator={','}
              decimalScale={5}
              placeholder={variant.sellingPrice} />
          </div>
          <div className="column">
            {!!variant.hasRelated && (
              <div
                data-tip
                data-for={`config-disabled-tip-${variant.id}`}>
                <button
                  type="button"
                  className="button small"
                  disabled={!variantData.checked}
                  onClick={() => navigateToVariant(variant)}>
                  <i className="fa fa-cog fa-lg" />
                </button>
                {!variantData.checked && (
                  <ReactTooltip
                    id={`config-disabled-tip-${variant.id}`}
                    effect="solid">
                    <span>Varianten måste vara valbar för att kunna konfigureras</span>
                  </ReactTooltip>
                )}
              </div>
            )}
          </div>
        </div>
        {!!variantData.expanded && !!variantData.related && !!variantData.related.length && (() => {
          const checked = hasChecked(modifiedData);
          const checkedProducts = _.filter(p =>
            _.some(checked, p.variants), variantData.related);
          return (
            <div className="row related-row">
              <div className="column">
                {_.map(p => (
                  <ReadonlyProduct
                    key={p.id}
                    product={p}
                    toggleVariant={toggleVariant}
                    modifiedData={modifiedData} />
                ), !!checkedProducts.length ? checkedProducts : variantData.related)}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export type ProductProps = {
  product: Object,
  productType: Object,
  navigateToVariant: Function,
  updateRelatedVariant: Function,
  toggleVariant: Function,
  setStandardProduct: Function,
  modifiedData: Object,
  navigationStack: Object,
  toggleProduct: Function,
  isStandard: boolean,
  clearStandardProduct: Function,
  checkProduct: Function,
  updateVariants: Function,
};

const Product = ({
  product,
  productType,
  navigateToVariant,
  updateRelatedVariant,
  toggleVariant,
  setStandardProduct,
  modifiedData,
  navigationStack,
  toggleProduct,
  isStandard,
  clearStandardProduct,
  checkProduct,
  updateVariants,
}: ProductProps) => {
  const selectionCount = _.filter(v => !!modifiedData[v.uuid] && !!modifiedData[v.uuid].checked, product.variants).length;
  const firstWithQuantity = _.flow(
    _.map(v => modifiedData[v.uuid]),
    _.filter(v => !!v && v.quantity !== null),
  )(product.variants);
  const firstQuantity = !!firstWithQuantity.length
    ? firstWithQuantity[0].quantity
    : null;
  const firstWithUnit = _.flow(
    _.map(v => modifiedData[v.uuid]),
    _.filter(v => !!v && !!v.unit),
  )(product.variants);
  const firstUnit = !!firstWithUnit.length
    ? firstWithUnit[0].unit || ''
    : '';
  return (
    <div className="row">
      <div className="column">
        <div className="row center-vert">
          <div
            className="shrink column product-toggle"
            onClick={() => toggleProduct(product)}>
            <i className={`fa fa-${!!product.expanded ? 'minus' : 'plus'}`} />
          </div>
          <div className="shrink column product-standard">
            <Checkbox
              name={`product-${product.id}-standard`}
              checked={!!isStandard}
              onChange={e => {
                if (!!e.target.checked) {
                  setStandardProduct(product, productType);
                } else {
                  clearStandardProduct(productType);
                }
              }}
              id={`product-${product.id}-standard`} />
            <label htmlFor={`product-${product.id}-standard`}>
              Standard
            </label>
          </div>
          <div className="shrink column product-checked">
            <Checkbox
              name={`product-${product.id}-enabled`}
              checked={selectionCount === product.variants.length
                ? true
                : selectionCount > 0
                  ? null
                  : false
              }
              onChange={e => checkProduct(product.id, !!e.target.checked)}
              id={`product-${product.id}-enabled`} />
            <label htmlFor={`product-${product.id}-enabled`}>
              Välj alla
            </label>
          </div>
          <div className="small-4 column product-name">
            {product.name}
          </div>
          <div className="small-1 column">
            <div className="quantity-unit-label">
              Antal
            </div>
            <NumberFormat
              value={firstQuantity}
              onValueChange={(val, evt) => {
                updateVariants(product.id, {
                  checked: true,
                  quantity: _.isEmpty(val.value) ? null : val.floatValue,
                });
              }}
              decimalSeparator={','}
              decimalScale={5}
              placeholder="Antal" />
          </div>
          <div className="small-2 column">
            <div className="quantity-unit-label">
              Enhet
            </div>
            <input
              type="text"
              placeholder="Enhet"
              value={firstUnit}
              onChange={e => updateVariants(product.id, {
                checked: true,
                unit: e.target.value,
              })} />
          </div>
        </div>
        {!!product.expanded && (
          <div className="row variants-row">
            <div className="column">
              {_.map(v => (
                <Variant
                  key={v.id}
                  variant={v}
                  navigateToVariant={navigateToVariant}
                  updateRelatedVariant={updateRelatedVariant}
                  toggleVariant={toggleVariant}
                  setStandardProduct={setStandardProduct}
                  modifiedData={modifiedData}
                  isStandard={isStandard}
                  mainVariantId={product.mainVariantId} />
              ), product.variants)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export type ProductTypeProps = {
  productType: Object,
  navigateToVariant: Function,
  updateRelatedVariant: Function,
  toggleVariant: Function,
  setStandardProduct: Function,
  modifiedData: Object,
  navigationStack: Object,
  toggleProduct: Function,
  clearStandardProduct: Function,
  isLast: boolean,
  checkProduct: Function,
  updateVariants: Function,
  updateVariantsForType: Function,
};

const ProductType = ({
  productType,
  navigateToVariant,
  updateRelatedVariant,
  toggleVariant,
  setStandardProduct,
  modifiedData,
  navigationStack,
  toggleProduct,
  clearStandardProduct,
  isLast,
  checkProduct,
  updateVariants,
  updateVariantsForType,
}: ProductTypeProps) => {
  const allVariants = _.flatMap(p => p.variants, productType.products);
  const firstWithQuantity = _.flow(
    _.map(v => modifiedData[v.uuid]),
    _.filter(v => !!v && v.quantity !== null),
  )(allVariants);
  const firstQuantity = !!firstWithQuantity.length
    ? firstWithQuantity[0].quantity
    : null;
  const firstWithUnit = _.flow(
    _.map(v => modifiedData[v.uuid]),
    _.filter(v => !!v && !!v.unit),
  )(allVariants);
  const firstUnit = !!firstWithUnit.length
    ? firstWithUnit[0].unit || ''
    : '';
  return (
    <div className={classNames({
      row: true,
      'product-type-separator': !isLast,
    })}>
      <div className="column">
        <div className="row product-type-row center-vert">
          <div className="small-4 column product-type-name">
            {productType.name}
          </div>
          <div className="shrink column filler-col"></div>
          <div
            className="small-1 column small-offset-2"
            data-tip
            data-for={`type-override-tip-${productType.id}`}>
            <div className="quantity-unit-label">
              Antal
            </div>
            <NumberFormat
              value={firstQuantity}
              onValueChange={(val, evt) => {
                updateVariantsForType(productType.id, {
                  checked: true,
                  quantity: _.isEmpty(val.value) ? null : val.floatValue,
                });
              }}
              decimalSeparator={','}
              decimalScale={5}
              placeholder="Antal" />
          </div>
          <div
            className="small-2 column"
            data-tip
            data-for={`type-override-tip-${productType.id}`}>
            <div className="quantity-unit-label">
              Enhet
            </div>
            <input
              type="text"
              placeholder="Enhet"
              value={firstUnit}
              onChange={e => updateVariantsForType(productType.id, {
                checked: true,
                unit: e.target.value,
              })} />
          </div>
          <ReactTooltip
            id={`type-override-tip-${productType.id}`}
            effect="solid">
            <span>Ändringar i detta fält återspeglas i alla produkter nedan för denna produkttyp</span>
          </ReactTooltip>
        </div>
        <div className="row products-row">
          <div className="column">
            {_.map(p => (
              <Product
                key={p.id}
                product={p}
                isStandard={navigationStack.standardProducts[productType.id] === p.id}
                productType={productType}
                navigateToVariant={navigateToVariant}
                updateRelatedVariant={updateRelatedVariant}
                toggleVariant={toggleVariant}
                setStandardProduct={setStandardProduct}
                modifiedData={modifiedData}
                navigationStack={navigationStack}
                toggleProduct={toggleProduct}
                clearStandardProduct={clearStandardProduct}
                checkProduct={checkProduct}
                updateVariants={updateVariants} />
            ), productType.products)}
          </div>
        </div>
      </div>
    </div>
  );
};

export type BreadcrumbProps = {
  stack: Object,
  popDialogNavigation: Function,
  depth: number,
};

const Breadcrumb = ({
  stack,
  popDialogNavigation,
  depth,
}: BreadcrumbProps) => (
  <span>
    {!!stack.previous && (
      <Breadcrumb
        stack={stack.previous}
        popDialogNavigation={popDialogNavigation}
        depth={depth + 1} />
    )}
    {!!stack.previous && ' / '}
    <a
      href=""
      onClick={evt => {evt.preventDefault(); popDialogNavigation(depth);}}>
      {stack.name}
    </a>
  </span>
);

export type BreadcrumbRootProps = {
  stack: Object,
  popDialogNavigation: Function,
};

const BreadcrumbRoot = ({
  stack,
  popDialogNavigation,
}: BreadcrumbRootProps) => (
  <span>
    <Button
      icon="chevron-left"
      disabled={!stack.previous}
      onClick={() => popDialogNavigation(1)} />
    <span>
      {!!stack.previous && (
        <Breadcrumb
          stack={stack.previous}
          popDialogNavigation={popDialogNavigation}
          depth={1} />
      )}
      {!!stack.previous && ' / '}
      <span className="current-stack">{stack.name}</span>
    </span>
  </span>
);

export type AdvancedProductSlotDialogProps = {
  isFetching: boolean,
  error: ?any,
  onClose: Function,
  navigateToVariant: Function,
  updateRelatedVariant: Function,
  popDialogNavigation: Function,
  navigationStack: Object,
  toggleVariant: Function,
  setStandardProduct: Function,
  modifiedData: Object,
  toggleProduct: Function,
  sharedChecked: ?boolean,
  checkAllVariants: Function,
  clearStandardProduct: Function,
  checkProduct: Function,
  updateVariants: Function,
  updateVariantsForType: Function,
};

const AdvancedProductSlotDialog = ({
  isFetching,
  error,
  onClose,
  navigateToVariant,
  popDialogNavigation,
  navigationStack,
  updateRelatedVariant,
  toggleVariant,
  setStandardProduct,
  modifiedData,
  toggleProduct,
  sharedChecked,
  checkAllVariants,
  clearStandardProduct,
  checkProduct,
  updateVariants,
  updateVariantsForType,
}: AdvancedProductSlotDialogProps) => (
  <div>
    <ModalDialog
      onClose={onClose}
      className="AdvancedProductSlotDialog"
      show={true}
      showCloseButton={true}
      size="large">
      <LoadingIndicator isLoading={isFetching} />
      <ServerError error={error} />
      <div className="dialog-title">
        <BreadcrumbRoot
          stack={navigationStack}
          popDialogNavigation={popDialogNavigation} />
      </div>
      {!!navigationStack.active && (
        <div className="row">
          <div className="column">
            <div className="row variants-row">
              <div className="column">
                <div className="row">
                  <div className="column">
                    <div className="row variant-row">
                      <div className="shrink column">
                        <div className="separator"></div>
                      </div>
                      <div className="small-offset-4 column">
                        <Checkbox
                          checked={sharedChecked}
                          onChange={e => checkAllVariants(e.target.checked)}
                          id="check-all" />
                        <label htmlFor="check-all">
                          Välj alla
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {mapWithIndex((t, index) => (
        <ProductType
          key={t.id}
          productType={t}
          navigateToVariant={navigateToVariant}
          updateRelatedVariant={updateRelatedVariant}
          toggleVariant={toggleVariant}
          setStandardProduct={setStandardProduct}
          modifiedData={modifiedData}
          navigationStack={navigationStack}
          toggleProduct={toggleProduct}
          clearStandardProduct={clearStandardProduct}
          checkProduct={checkProduct}
          updateVariants={updateVariants}
          updateVariantsForType={updateVariantsForType}
          isLast={index === (navigationStack.active.length - 1)}/>
      ), navigationStack.active)}
      <div className="row">
        <div className="column dialog-toolbar">
          <button
            type="button"
            className="button"
            onClick={onClose}>
            Stäng
          </button>
        </div>
      </div>
    </ModalDialog>
  </div>
);

export default AdvancedProductSlotDialog;



// WEBPACK FOOTER //
// ./src/components/AdvancedProductSlotDialog/AdvancedProductSlotDialog.js